import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import WebsiteNavbar from '../../components/WebsiteUI/WebsiteNavbar';
import WebsiteFooter from '../../components/WebsiteUI/WebsiteFooter';
import CTASection from '../../components/WebsiteUI/CTASection';
import FAQ from '../../components/WebsiteUI/FAQ';
import { faqItems } from '../../data/pricing';
import { contactUs } from '../../services/auth';
import styles from './ContactPage.module.css';
import './ContactForm.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    acceptedTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateField = (name, value) => {
    if (name === 'name' && !value.trim()) return 'Please enter your name';
    if (name === 'email') {
      if (!value.trim()) return 'Email is required';
      if (!validateEmail(value)) return 'Please enter a valid email address';
    }
    if (name === 'message' && !value.trim()) return 'Please enter a message';
    if (name === 'acceptedTerms' && !value) return 'You must accept the terms to continue';
    return '';
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (errors[name]) {
      const error = validateField(name, finalValue);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    const error = validateField(name, finalValue);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      await contactUs(formData);
      setIsSuccess(true);
      setFormData({ name: '', email: '', phone: '', message: '', acceptedTerms: false });
    } catch (err) {
      // contactUs handles error display via swal
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <WebsiteNavbar />
      <section className={styles.contact}>
        <div className={styles.contact__container}>
          <div className={styles.contact__info}>
            <div className={styles.contact__text}>
              <h1 className={styles.contact__title}>Contact Us</h1>
              <p className={styles.contact__subtitle}>
                Whether you have questions about our platform, need support, or
                want to learn more about how Ogiso can benefit your agency, our
                team is here to assist you.
              </p>
            </div>

            <div className={styles.contact__details}>
              <a href="mailto:support@ogiso.io" className={styles.contact__detail}>
                <span className={styles.contact__detailIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span className={styles.contact__detailText}>support@ogiso.io</span>
              </a>

              <a href="tel:+17576000297" className={styles.contact__detail}>
                <span className={styles.contact__detailIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 16.92V19.92C22.0011 20.1986 21.9441 20.4742 21.8325 20.7294C21.7209 20.9846 21.5573 21.2137 21.3521 21.402C21.1468 21.5902 20.9046 21.7336 20.6407 21.8228C20.3769 21.912 20.0974 21.9452 19.82 21.92C16.7428 21.5857 13.787 20.5342 11.19 18.81C8.77382 17.2459 6.72907 15.2012 5.165 12.785C3.44081 10.188 2.38933 7.23223 2.055 4.15501C2.0298 3.8776 2.06299 3.59811 2.15217 3.33426C2.24135 3.07042 2.38459 2.82823 2.57285 2.62298C2.76111 2.41774 2.99023 2.25411 3.24545 2.14253C3.50067 2.03095 3.7763 1.97397 4.055 1.97501H7.055C7.5492 1.97059 8.02677 2.14995 8.40188 2.48074C8.77699 2.81152 9.02322 3.27043 9.095 3.77501C9.22877 4.78923 9.47545 5.78762 9.83 6.75001C9.97148 7.13289 9.99967 7.54699 9.91038 7.94276C9.82109 8.33852 9.61794 8.69931 9.325 8.98001L8.055 10.25C9.47761 12.7523 11.5527 14.8274 14.055 16.25L15.325 14.98C15.6057 14.6871 15.9665 14.4839 16.3622 14.3946C16.758 14.3053 17.1721 14.3335 17.555 14.475C18.5174 14.8296 19.5158 15.0762 20.53 15.21C21.0366 15.2825 21.4967 15.5306 21.8279 15.9082C22.159 16.2858 22.3377 16.7661 22.33 17.265V16.92Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span className={styles.contact__detailText}>+1 757-600-0297</span>
              </a>

              <div className={styles.contact__detail}>
                <span className={styles.contact__detailIcon}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <span className={styles.contact__detailText}>2436 West Tonto St, Phoenix, AZ 85009</span>
              </div>
            </div>
          </div>

          <div className={styles.contact__form}>
            {isSuccess ? (
              <div className="contact-form__success">
                <h3 className="contact-form__success-title">Message sent</h3>
                <p className="contact-form__success-text">
                  Thank you for reaching out. We'll get back to you shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setIsSuccess(false)}
                  className="contact-form__success-button"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="contact-form__row">
                  <div className="contact-form__field">
                    <label htmlFor="name" className="contact-form__label">Full Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`contact-form__input ${errors.name ? 'contact-form__input--error' : ''}`}
                    />
                    {errors.name && <span className="contact-form__error">{errors.name}</span>}
                  </div>
                  <div className="contact-form__field">
                    <label htmlFor="email" className="contact-form__label">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`contact-form__input ${errors.email ? 'contact-form__input--error' : ''}`}
                    />
                    {errors.email && <span className="contact-form__error">{errors.email}</span>}
                  </div>
                </div>

                <div className="contact-form__field">
                  <label htmlFor="phone" className="contact-form__label">Phone Number (Optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="+1 (555) 000-0000"
                    value={formData.phone}
                    onChange={handleChange}
                    className="contact-form__input"
                  />
                </div>

                <div className="contact-form__field">
                  <label htmlFor="message" className="contact-form__label">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    placeholder="Tell us about your needs..."
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`contact-form__textarea ${errors.message ? 'contact-form__textarea--error' : ''}`}
                  />
                  {errors.message && <span className="contact-form__error">{errors.message}</span>}
                </div>

                <div className="contact-form__field">
                  <label className="contact-form__checkbox-label">
                    <div className="contact-form__checkbox-wrapper">
                      <input
                        type="checkbox"
                        name="acceptedTerms"
                        checked={formData.acceptedTerms}
                        onChange={handleChange}
                        className={`contact-form__checkbox ${errors.acceptedTerms ? 'contact-form__checkbox--error' : ''}`}
                      />
                      <svg
                        className="contact-form__checkbox-icon"
                        viewBox="0 0 12 10"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="1.5 5 4.5 8 10.5 2" />
                      </svg>
                    </div>
                    <span className={`contact-form__checkbox-text ${errors.acceptedTerms ? 'contact-form__checkbox-text--error' : ''}`}>
                      I agree to the{' '}
                      <Link to="/privacy-policy" className="contact-form__link">Privacy Policy</Link>{' '}
                      and{' '}
                      <Link to="/term-conditions" className="contact-form__link">Terms of Service</Link>.
                    </span>
                  </label>
                </div>

                <button type="submit" disabled={isSubmitting} className="contact-form__submit">
                  {isSubmitting ? (
                    <>
                      <span className="contact-form__spinner" />
                      Sending...
                    </>
                  ) : (
                    'Submit'
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <section className={styles.contact__faq}>
        <div className={styles.contact__faqContainer}>
          <FAQ
            title="Frequently asked questions"
            description="Everything you need to know about the product and billing."
            items={faqItems}
          />
        </div>
      </section>

      <CTASection />
      <WebsiteFooter />
    </>
  );
};

export default ContactPage;
